import React, { useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useReseller } from '../../contexts/ResellerContext';

const DEFAULT_PRIMARY = '#00AAFF';

function parseThemeColor(raw) {
  if (!raw?.trim()?.startsWith('#')) return DEFAULT_PRIMARY;
  const hex6 = raw.trim().match(/^#[0-9A-Fa-f]{6}$/)?.[0];
  const hex3 = raw.trim().match(/^#[0-9A-Fa-f]{3}$/)?.[0];
  if (hex6) return hex6;
  if (hex3) return hex3.replace(/([0-9A-Fa-f])/g, '$1$1');
  return DEFAULT_PRIMARY;
}

function darkenHex(hex, amount = 0.15) {
  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return hex;
  const r = Math.max(0, Math.round(parseInt(match[1], 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(match[2], 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(match[3], 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function getPrimaryColor(reseller) {
  return reseller?.themeColor ? parseThemeColor(reseller.themeColor) : DEFAULT_PRIMARY;
}

export function buildResellerTheme(primaryColor) {
  const primaryDark = darkenHex(primaryColor, 0.15);
  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
        dark: primaryDark,
        contrastText: '#FFFFFF',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: primaryColor,
            '&:hover': {
              backgroundColor: primaryDark,
            },
          },
          outlinedPrimary: {
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryDark,
              backgroundColor: alpha(primaryColor, 0.08),
            },
          },
          textPrimary: {
            color: primaryColor,
            '&:hover': {
              backgroundColor: alpha(primaryColor, 0.08),
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&.MuiIconButton-colorPrimary': {
              color: primaryColor,
            },
            '&.MuiIconButton-colorPrimary:hover': {
              backgroundColor: alpha(primaryColor, 0.08),
              color: primaryDark,
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            '&.MuiFab-colorPrimary': {
              backgroundColor: primaryColor,
            },
            '&.MuiFab-colorPrimary:hover': {
              backgroundColor: primaryDark,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '&.MuiSwitch-colorPrimary': {
              '&.Mui-checked': {
                color: primaryColor,
              },
            },
          },
          switchBase: {
            '&.MuiSwitch-switchBase.Mui-checked': {
              color: primaryColor,
              '& + .MuiSwitch-track': {
                backgroundColor: primaryColor,
              },
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: primaryColor,
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 600,
        lg: 1200,
        xl: 1536,
      },
    },
  });
}

export function useResellerTheme() {
  const { reseller } = useReseller();
  const primaryColor = getPrimaryColor(reseller);
  return useMemo(() => buildResellerTheme(primaryColor), [primaryColor]);
}

function ThemeWrapper({ children }) {
  const theme = useResellerTheme();

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', theme.palette.primary.main);
  }, [theme.palette.primary.main]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default ThemeWrapper;
